import { useLazyQuery, useMutation } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import Loop from "@mui/icons-material/Loop";
import Work from "@mui/icons-material/Work";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  JobStatus,
  JobTask,
  StaffReportJobsQuery,
  StaffReportType,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo, useState } from "react";
import { prettyPrintDate, prettyPrintDateTime } from "util/dates";
import { downloadFromUrl } from "util/download";

import { NewlyNotReportingFacilitiesDialog } from "./NewlyNotReportingFacilitiesDialog";

export const REPORT_TYPE_GROUPS: {
  category: string;
  reports: { value: StaffReportType; label: string; usesReportingYear?: boolean }[];
}[] = [
  {
    category: "Tier II",
    reports: [
      {
        value: StaffReportType.CersReportSubmissionStatus,
        label: "CERS Report Submission Status",
      },
      { value: StaffReportType.FacilityContacts, label: "Facility Contacts" },
      { value: StaffReportType.FacilityCredentials, label: "Facility Credentials" },
      {
        value: StaffReportType.FacilitiesNeedingImplementation,
        label: "Facilities Needing Implementation",
      },
      {
        value: StaffReportType.NewlyNotReportingFacilities,
        label: "Newly Not Reporting Facilities",
      },
      { value: StaffReportType.NewlyReporting, label: "Newly Reporting" },
      { value: StaffReportType.NotReportingAnalysis, label: "Not Reporting Analysis" },
      { value: StaffReportType.NycRmpFacilities, label: "NYC RMP Facilities" },
      { value: StaffReportType.Rbr, label: "RBR" },
      { value: StaffReportType.TierIiReportAssignees, label: "Tier II Report Assignees" },
      { value: StaffReportType.ReportsNeedingRevision, label: "Reports Needing Revision" },
      { value: StaffReportType.SubmissionRejections, label: "Submission Rejections" },
      { value: StaffReportType.TierIiValidation, label: "Tier II Validation" },
    ],
  },
  {
    category: "Waste",
    reports: [
      {
        value: StaffReportType.GeneratorStatusDeliverable,
        label: "Generator Status Deliverable",
        usesReportingYear: false,
      },
      { value: StaffReportType.HighestGeneratorStatus, label: "Highest Generator Status" },
      {
        value: StaffReportType.KyRcraRenewalDates,
        label: "KY RCRA Renewal Dates",
        usesReportingYear: false,
      },
      { value: StaffReportType.RcraInfoFlatFile, label: "RCRAInfo Flat File" },
      {
        value: StaffReportType.RcraInfoSiteManagerAccess,
        label: "RCRAInfo Site Manager Access",
      },
      { value: StaffReportType.RcraInfoValidation, label: "RCRAInfo Validation" },
      { value: StaffReportType.RequirementDefinitions, label: "Requirement Definitions" },
      { value: StaffReportType.YtdOverThreshold, label: "YTD Over Threshold" },
    ],
  },
  {
    category: "Tenant / Encamp",
    reports: [
      { value: StaffReportType.BillingAnalysis, label: "Billing Analysis" },
      { value: StaffReportType.CancelledPayments, label: "Cancelled Payments" },
      { value: StaffReportType.CustomerUserSignIns, label: "Customer User Sign-ins" },
      { value: StaffReportType.FacilityBilling, label: "Facility Billing" },
      {
        value: StaffReportType.SubmissionHistory,
        label: "Submission History",
        usesReportingYear: false,
      },
      { value: StaffReportType.UserReport, label: "User Report", usesReportingYear: false },
    ],
  },
];

const REPORTS_WITHOUT_REPORTING_YEAR = new Set(
  REPORT_TYPE_GROUPS.flatMap((g) =>
    g.reports.filter((r) => r.usesReportingYear === false).map((r) => r.value)
  )
);

export const REPORT_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  REPORT_TYPE_GROUPS.flatMap((g) => g.reports.map((r) => [r.value, r.label]))
);

gql(`
  fragment StaffReportJobFragment on Job {
    id
    task
    action
    status
    updatedAt
    totalIssues
    staffReportKey
    parameters
    tenant {
      id
      name
    }
    user {
      person {
        first
        last
      }
    }
  }
`);

const STAFF_REPORTS = gql(`
  query StaffReportJobs($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    jobs(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        ...StaffReportJobFragment
      }
    }
  }
`);

const GET_DOCUMENT_DOWNLOAD_LINK_QUERY = gql(`
  query GetStaffReportDownloadLink($id: ID!) {
    job(id: $id) {
      staffReportDownloadLink
      output
    }
  }
`);

const GENERATE_STAFF_REPORT = gql(`
  mutation GenerateStaffReport($reportType: StaffReportType!, $reportingYear: Int, $startDate: DateTime, $endDate: DateTime, $tenantId: String, $includeNonReportableWaste: Boolean, $ignoreWasteValidationIssues: Boolean, $includeInactiveUsers: Boolean, $includeDownwardChanges: Boolean) {
    generateStaffReport(reportType: $reportType, reportingYear: $reportingYear, startDate: $startDate, endDate: $endDate, tenantId: $tenantId, includeInactiveUsers: $includeInactiveUsers, includeNonReportableWaste: $includeNonReportableWaste, ignoreWasteValidationIssues: $ignoreWasteValidationIssues, includeDownwardChanges: $includeDownwardChanges) {
      ...StaffReportJobFragment
    }
  }
`);

type Row = StaffReportJobsQuery["jobs"]["items"][number];

/**
 * Converts a date to midnight UTC of the same calendar date.
 * This ensures consistent date handling regardless of user timezone.
 */
const toMidnightUTC = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
};

export const StaffReports = () => {
  const { tenantId, tenant } = useTenant();
  const alerts = useAlerts();

  const [getDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    onCompleted: ({ job: { staffReportDownloadLink, output } }) => {
      if (!staffReportDownloadLink) {
        alerts.info(output.message ?? "No report was created.");
      } else {
        downloadFromUrl(staffReportDownloadLink);
      }
    },
    onError(error) {
      alerts.error("An error occurred while downloading the report", error);
    },
  });
  const [reportHasBeenGenerated, setReportHasBeenGenerated] = useState(false);
  const [reportType, setReportType] = useState<StaffReportType | null>(null);
  const [reportingYear, setReportingYear] = useState<number | null>(useSelectedReportingYear());
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(currentTierIIReportingYear, 0, 1)
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const [usersAddedSince, setUsersAddedSince] = useState<Date | null>(null);
  const [includeInactiveUsers, setIncludeInactiveUsers] = useState(false);
  const [runForAllTenants, setRunForAllTenants] = useState(false);
  const [includeNonReportableWaste, setIncludeNonReportableWaste] = useState(false);
  const [ignoreWasteValidationIssues, setIgnoreWasteValidationIssues] = useState(false);
  const [includeDownwardChanges, setIncludeDownwardChanges] = useState(false);

  const [generateReport, { loading }] = useMutation(GENERATE_STAFF_REPORT, {
    refetchQueries: [STAFF_REPORTS],
    onCompleted: () => {
      setGenerateReportOpen(false);
      setReportHasBeenGenerated(true);
    },
    onError: (error) => {
      alerts.error("Error generating report", error);
      setGenerateReportOpen(false);
    },
  });
  const [generateReportOpen, setGenerateReportOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const {
    filters: { ry },
    setFilters,
  } = useQueryParams<{ ry: string }>();

  const reportIsTenanted: boolean = useMemo(() => {
    if (reportType === null) {
      return true;
    }

    const nonTenantedReports = [
      StaffReportType.BillingAnalysis,
      StaffReportType.CancelledPayments,
      StaffReportType.CustomerUserSignIns,
      StaffReportType.FacilityBilling,
      StaffReportType.Rbr,
      StaffReportType.SubmissionRejections,
      StaffReportType.UserReport,
      StaffReportType.TierIiReportAssignees,
      StaffReportType.RequirementDefinitions,
      StaffReportType.KyRcraRenewalDates,
    ];

    // If running certain reports for all tenants, don't tenate it
    if (
      (reportType === StaffReportType.NotReportingAnalysis ||
        reportType === StaffReportType.NewlyReporting ||
        reportType === StaffReportType.HighestGeneratorStatus ||
        reportType === StaffReportType.ReportsNeedingRevision ||
        reportType === StaffReportType.FacilitiesNeedingImplementation) &&
      runForAllTenants
    ) {
      return false;
    }

    return !nonTenantedReports.includes(reportType);
  }, [reportType, runForAllTenants]);

  const usesReportingYear = reportType !== null && !REPORTS_WITHOUT_REPORTING_YEAR.has(reportType);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "tenantName",
        headerName: "Organization",
        flex: 1,
        valueGetter: (_, row) => row.tenant?.name ?? "All tenants",
      },
      {
        field: "reportType",
        headerName: "Report Type",
        flex: 1,
        valueGetter: (_, row) => {
          if (row.task === JobTask.ExportData) {
            // For EXPORT_DATA jobs, display the export type from parameters
            const exportType = row.parameters["exportType"];
            if (!exportType) return "Export Data";
            const label = `Export: ${prettyPrintEnumValue(exportType)}`;
            // Clarify single-facility exports vs tenant-wide staff reports
            if (row.parameters["facilityId"]) {
              return `${label} (Single-Facility)`;
            }
            return label;
          }

          const reportType = row.parameters["reportType"];
          return REPORT_TYPE_LABEL[reportType] ?? reportType;
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
      },
      {
        field: "updatedAt",
        headerName: "Generated On",
        valueFormatter: (_, row) => prettyPrintDateTime(row.updatedAt),
        flex: 1,
        filterKeyType: "time",
      },
      {
        field: "totalIssues",
        headerName: "Issues",
        headerAlign: "center",
        align: "center",
        renderCell: ({ value }) => <IssueCount issueCount={value} />,
        filterKeyType: "number",
      },
      {
        field: "reportingYear",
        headerName: "Reporting Year",
        flex: 1,
        valueGetter: (_, row) => row.parameters["reportingYear"],
      },
      {
        field: "generatedBy",
        headerName: "Generated By",
        flex: 1,
        valueGetter: (_, row) => `${row.user?.person?.first ?? ""} ${row.user?.person?.last ?? ""}`,
      },
      {
        field: "startDate",
        headerName: "Start Date",
        flex: 1,
        valueGetter: (_, row) => row.parameters["startDate"],
        valueFormatter: (_, row) =>
          row.parameters["startDate"] ? prettyPrintDate(row.parameters["startDate"]) : "",
      },
      {
        field: "endDate",
        headerName: "End Date",
        flex: 1,
        valueGetter: (_, row) => row.parameters["endDate"],
        valueFormatter: (_, row) =>
          row.parameters["endDate"] ? prettyPrintDate(row.parameters["endDate"]) : "",
      },
      {
        field: "actions",
        type: "actions",
        getActions({ row }) {
          const actions = [
            <GridActionsCellItem
              key="goToJob"
              label="Go to Job"
              icon={<Work />}
              onClick={() => {
                window.open(`/staff/jobs/${row.id}`, "_blank");
              }}
            />,
          ];
          if (row.status === JobStatus.Succeeded)
            actions.push(
              <GridActionsCellItem
                key="download"
                label="Download"
                icon={<Download />}
                onClick={() =>
                  getDownloadLink({
                    variables: { id: row.id },
                  })
                }
              />
            );

          if (row.status === JobStatus.Queued || row.status === JobStatus.Running)
            actions.push(
              <GridActionsCellItem key="queued" label="Queued" disabled icon={<Loop />} />
            );

          return actions;
        },
      },
    ],
    [getDownloadLink]
  );

  const shouldPoll = useCallback((data: StaffReportJobsQuery) => {
    const polling = data.jobs.items.some((j) =>
      [JobStatus.Running, JobStatus.Queued, JobStatus.ProvisioningEcsTask].includes(
        j.status as JobStatus
      )
    );
    return polling ? 2000 : undefined;
  }, []);

  const shouldDisableGenerateButton = useMemo(() => {
    if (reportType === null) {
      return true;
    }
    if (reportHasBeenGenerated) {
      return true;
    }
    return false;
  }, [reportType, reportHasBeenGenerated]);

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            mb: reportType === StaffReportType.BillingAnalysis ? 2 : 0,
          }}
        >
          <FormControl sx={{ width: 275, mr: 2 }}>
            <InputLabel shrink>Report Type</InputLabel>
            <Select
              value={reportType ?? ""}
              label="Report Type"
              displayEmpty
              notched
              renderValue={(selected) => {
                if (!selected) {
                  return <Typography color="text.secondary">Select a report type</Typography>;
                }
                return REPORT_TYPE_LABEL[selected] ?? selected;
              }}
              onChange={(e) => {
                setReportType(e.target.value as StaffReportType);
                setReportHasBeenGenerated(false);
                setRunForAllTenants(false);
              }}
            >
              {REPORT_TYPE_GROUPS.map(({ category, reports }) => [
                <ListSubheader key={category}>{category}</ListSubheader>,
                ...reports.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          <Button
            key="generate"
            variant="contained"
            disabled={shouldDisableGenerateButton}
            onClick={() => setGenerateReportOpen(true)}
          >
            Generate Report
          </Button>
          {reportType === StaffReportType.NewlyNotReportingFacilities && (
            <Button
              key="assistant"
              variant="outlined"
              sx={{ ml: 1 }}
              onClick={() => setAssistantOpen(true)}
            >
              Open Newly Not Reporting Assistant
            </Button>
          )}
        </Box>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {usesReportingYear && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <TierIIYearPicker
                  value={reportingYear !== null ? String(reportingYear) : ""}
                  onChange={(val) => {
                    if (val === "" || val === null || isNaN(Number(val))) {
                      setReportingYear(null);
                      setFilters((f) => {
                        f.ry = undefined;
                      });
                    } else {
                      setReportingYear(Number(val));
                      setFilters((f) => {
                        f.ry = String(val);
                      });
                    }
                  }}
                  showNoneOption={reportType === StaffReportType.BillingAnalysis}
                  tooltipText={
                    reportType === StaffReportType.BillingAnalysis
                      ? 'Reporting Year only applies to "Annual" reports for this report'
                      : undefined
                  }
                  selectProps={{
                    variant: "outlined",
                    size: "medium",
                    label: "Reporting Year",
                  }}
                />
              </FormControl>
            </Grid>
          )}
          {(reportType === StaffReportType.BillingAnalysis ||
            reportType === StaffReportType.CustomerUserSignIns) && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>
          )}

          {(reportType === StaffReportType.NotReportingAnalysis ||
            reportType === StaffReportType.NewlyReporting ||
            reportType === StaffReportType.HighestGeneratorStatus ||
            reportType === StaffReportType.ReportsNeedingRevision ||
            reportType === StaffReportType.FacilitiesNeedingImplementation) && (
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runForAllTenants}
                    onChange={(e) => {
                      setRunForAllTenants(e.target.checked);
                      setReportHasBeenGenerated(false);
                    }}
                  />
                }
                label="Run for all tenants"
              />
            </Grid>
          )}

          {reportType === StaffReportType.UserReport && (
            <>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label="Added Since"
                    value={usersAddedSince}
                    onChange={(date) => setUsersAddedSince(date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <Typography pt={1} variant="body2">
                    If no date is provided, the report will return all users
                  </Typography>
                </Grid>
              </LocalizationProvider>
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                sx={{ display: "flex", alignItems: "flex-start", pt: 1 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeInactiveUsers}
                      onChange={(e) => {
                        setIncludeInactiveUsers(e.target.checked);
                        setReportHasBeenGenerated(false);
                      }}
                    />
                  }
                  label="Include inactive/disabled users"
                />
              </Grid>
            </>
          )}

          {reportType === StaffReportType.GeneratorStatusDeliverable && (
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeDownwardChanges}
                    onChange={(e) => {
                      setIncludeDownwardChanges(e.target.checked);
                      setReportHasBeenGenerated(false);
                    }}
                  />
                }
                label="Include downward changes"
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <OmnisearchDataGrid
        persistenceKey="staff-reports"
        isURLDriven
        withPadding={false}
        columns={columns}
        dataQuery={STAFF_REPORTS}
        defaultSearch={`staffReports:${tenantId}`}
        getItems={(data) => data.jobs.items}
        getCount={(data) => data.jobs.count}
        shouldPoll={shouldPoll}
        initialSortModel={[{ sort: "desc", field: "updatedAt" }]}
        excludeFilterColumns={["reportType", "tenantName"]}
      />
      <Dialog
        open={generateReportOpen}
        onClose={() => setGenerateReportOpen(false)}
        title="Generate Staff Report"
        primaryActionButton={
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => {
              const variables: any = {
                reportType: reportType,
                tenantId: reportIsTenanted ? tenantId : undefined,
                ...((reportType === StaffReportType.BillingAnalysis ||
                  reportType === StaffReportType.CustomerUserSignIns) && {
                  startDate: startDate ? toMidnightUTC(startDate) : null,
                  endDate: endDate ? toMidnightUTC(endDate) : null,
                }),
                ...(reportType === StaffReportType.UserReport && {
                  startDate: usersAddedSince,
                  includeInactiveUsers,
                }),
                ...((reportType === StaffReportType.RcraInfoFlatFile ||
                  reportType === StaffReportType.RcraInfoValidation) && {
                  includeNonReportableWaste,
                }),
                ...(reportType === StaffReportType.RcraInfoFlatFile && {
                  ignoreWasteValidationIssues,
                }),
                ...(reportType === StaffReportType.GeneratorStatusDeliverable && {
                  includeDownwardChanges,
                }),
              };
              if (usesReportingYear && reportingYear !== null) {
                variables.reportingYear = reportingYear;
              }
              generateReport({ variables });
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Generate"}
          </Button>
        }
      >
        This will generate a {reportType ? REPORT_TYPE_LABEL[reportType] : ""} report. Are you sure
        you want to generate a {reportType ? REPORT_TYPE_LABEL[reportType] : ""} report for{" "}
        {reportIsTenanted && tenant?.name ? tenant.name : "all tenants"}?
        {(reportType === StaffReportType.BillingAnalysis ||
          reportType === StaffReportType.CustomerUserSignIns) && (
          <Box sx={{ mt: 2 }}>
            <p>Start Date: {startDate ? startDate.toLocaleDateString() : "Not specified"}</p>
            <p>End Date: {endDate ? endDate.toLocaleDateString() : "Not specified"}</p>
          </Box>
        )}
        {(reportType === StaffReportType.RcraInfoFlatFile ||
          reportType === StaffReportType.RcraInfoValidation) && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeNonReportableWaste}
                  onChange={(e) => setIncludeNonReportableWaste(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Include non-reportable waste</Typography>}
            />
            {reportType === StaffReportType.RcraInfoFlatFile && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ignoreWasteValidationIssues}
                      onChange={(e) => setIgnoreWasteValidationIssues(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Ignore validation issues</Typography>}
                />
                <Typography variant="caption" color="text.secondary">
                  To review waste stream validation issues, generate an RCRAInfo Validation report.
                </Typography>
              </>
            )}
          </Box>
        )}
      </Dialog>
      <NewlyNotReportingFacilitiesDialog
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        reportingYear={reportingYear ?? currentTierIIReportingYear}
      />
    </>
  );
};
